import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search } from 'lucide-react';
import { createDebt, TIPOS_COMUNES, TIPOS_COLS } from '../api';
import { listMembers, MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { useToast } from '../../../context/ToastContext';
import { Tiles } from './PaymentForm';
import { VentaVitrinaModal } from './CashboxPanel';

// «Vitrina» no teclea un importe: abre el carrito y deja el adeudo con el detalle de artículos.
// Mismos cuadros y orden que «Registrar pago» (sin «Bono de clases», Carlos 29-sep-2026; «Otro» regresó 2-oct).
// «Desfasado» (Mabel 9/10-oct-2026): el resto de la mensualidad de quien entró a medio mes, que se cobra en SU
// fecha. Se guarda como mensualidad con sello «desfasado»: sale en su cuadro del Inicio 3 días antes y no multa.
// Su MES lo pone el backend con la fecha (2-nov-2026, #1628): es la mensualidad del mes en que vence, así que
// el selector de mes se oculta para no crear un segundo cargo de ese mes.
const TIPOS_DEUDA = [...TIPOS_COMUNES.slice(0, 1), { v: 'desfasado', label: 'Desfasado' }, ...TIPOS_COMUNES.slice(1)];

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

/** Mes en curso (YYYY-MM) en hora local. */
function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Modal para agregar un adeudo manual. onCreated() refresca la lista.
 *  Con `prefill` (desde la ficha del alumno) el alumno viene fijo. */
export default function DebtForm({ onClose, onCreated, prefill }: { onClose: () => void; onCreated: () => void; prefill?: MemberRow | null }) {
  const toast = useToast();
  const [picked, setPicked] = useState<MemberRow | null>(prefill ?? null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MemberRow[]>([]);
  const [openList, setOpenList] = useState(false);
  const [debtType, setDebtType] = useState('membership');
  const [amount, setAmount] = useState('');
  const [month, setMonth] = useState(mesActual());
  const [dueDate, setDueDate] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [vitrinaOpen, setVitrinaOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const elegirTipo = (v: string) => { if (v === 'product') { setVitrinaOpen(true); return; } setDebtType(v); };

  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      listMembers({ search: q.trim(), page_size: 8 }).then((r) => { setResults(r.data); setOpenList(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, picked]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpenList(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!picked) { toast.error('Selecciona un alumno'); return; }
    if (!(amt > 0)) { toast.error('Indica un monto válido'); return; }
    const desfasado = debtType === 'desfasado';
    if (!desfasado && month && !/^\d{4}-\d{2}$/.test(month)) { toast.error('El mes debe ser YYYY-MM'); return; }
    if (debtType === 'other' && !comment.trim()) { toast.error('Escribe el concepto: ¿qué debe?'); return; }
    if (desfasado && !dueDate) { toast.error('Indica la fecha en que le toca pagar el desfasado'); return; }
    setSaving(true);
    try {
      await createDebt({ account_id: picked.account_id, amount: amt, month: desfasado ? null : (month || null), due_date: dueDate || null,
        debt_type: desfasado ? 'membership' : debtType, comment: comment || (desfasado ? 'Cobro desfasado' : null), ...(desfasado ? { desfasado: true } : {}) });
      toast.success(desfasado ? 'Desfasado registrado · saldrá en el Inicio 3 días antes de su fecha' : 'Adeudo agregado');
      onCreated();
    } catch (err: any) { toast.error(err?.message || 'No se pudo agregar el adeudo'); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Registrar deuda</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {/* Desde la ficha del alumno el dueño de la deuda viene fijo: no hay que buscarlo. */}
          {prefill ? (
            <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
              <PersonAvatar contactId={prefill.account_id} hasPhoto={prefill.has_photo}
                name={prefill.firstname} lastName={prefill.lastname}
                size="h-7 w-7" ringClass="fill-surface-2" />
              <span className="flex-1 truncate text-sm text-content">{prefill.firstname} {prefill.lastname}</span>
            </div>
          ) : (
          <div ref={boxRef} className="relative">
            <label className={labelCls}>Alumno</label>
            {picked ? (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
                <PersonAvatar contactId={picked.account_id} hasPhoto={picked.has_photo}
                  name={picked.firstname} lastName={picked.lastname}
                  size="h-7 w-7" ringClass="fill-surface-2" />
                <span className="flex-1 truncate text-sm text-content">{picked.firstname} {picked.lastname}</span>
                <button type="button" onClick={() => { setPicked(null); setQ(''); }} className="text-content-3 hover:text-content"><X size={15} /></button>
              </div>
            ) : (
              <div className="relative">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpenList(true)}
                  placeholder="Buscar alumno por nombre…" className={`${inputCls} pl-9`} />
                {openList && results.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                    {results.map((m) => (
                      <button key={m.account_id} type="button" onClick={() => { setPicked(m); setOpenList(false); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-surface-2">
                        <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                          name={m.firstname} lastName={m.lastname}
                          size="h-6 w-6" initialClass="text-[10px]" />
                        <span className="truncate text-content">{m.firstname} {m.lastname}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Tipo de deuda en cuadritos (Karla 29-jul: igual que los de Kydemy) */}
          <div>
            <label className={labelCls}>Tipo de deuda</label>
            <Tiles value={debtType} options={TIPOS_DEUDA} onPick={elegirTipo} cols={TIPOS_COLS} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={debtType === 'desfasado' ? 'col-span-2' : ''}>
              <label className={labelCls}>Monto (MXN)</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            {debtType !== 'desfasado' && (
            <div>
              <label className={labelCls}>Mes (opcional)</label>
              <input value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-06" className={inputCls} />
            </div>
            )}
          </div>

          <div>
            <label className={labelCls}>{debtType === 'desfasado' ? 'Fecha en que le toca pagar' : 'Vence (opcional)'}</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>{debtType === 'other' ? 'Concepto (¿qué debe?)' : 'Concepto / nota (opcional)'}</label>
            <input data-testid="deuda-concepto" value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={debtType === 'other' ? 'Ej. Powerade que debía en Kydemy' : ''} className={inputCls} />
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : 'Registrar deuda'}
            </button>
          </div>
        </form>
      </div>
      {vitrinaOpen && (
        <VentaVitrinaModal paraFiar alumnaInicial={picked} onClose={() => setVitrinaOpen(false)}
          onSold={() => { setVitrinaOpen(false); onCreated(); }} />
      )}
    </div>,
    document.body
  );
}
